import { Constructor } from '@angular/cdk/table';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ICompany } from 'src/app/core/models/i-company';
import { IPerson } from 'src/app/core/models/i-person';
import { ITag } from 'src/app/core/models/i-tag';
import { ITopic } from 'src/app/core/models/i-topic';
import { compareArrays } from '../utils/array-utils';
import { TopicService } from 'src/app/content-management/shared/services/topic.service';
import { ITopicWithTranslations } from 'src/app/core/models/i-topic-with-translations';

type WithTaggingFormSectionType = {
  _initTagging(
    tags: ITag[],
    topics: ITopic[],
    companies: ICompany[],
    people: IPerson[],
    topicService: TopicService
  ): void;
  _initTaggingValueChanges(
    getPreviousTagsValue: () => ITag[] | null,
    getPreviousTopicsValue: () => ITopic[] | null,
    getPreviousCompanyValue: () => ICompany[] | null,
    getPreviousPeopleValue: () => IPerson[] | null
  ): void;

  _buildTags(): ITag[];
  _buildTopics(): ITopic[];
  _buildCompanies(): ICompany[];
  _buildPeople(): IPerson[];

  get tags(): ITag[];
  get topics(): ITopic[];
  get companies(): ICompany[];
  get people(): IPerson[];
};

type TaggingFormSectionRequerimentsBase = {
  form: FormGroup;
  FORM_PATHS: {
    TAGS: () => string;
    TOPICS: () => string;
    TOPICS_WITH_TRANSLATIONS: () => string;
    COMPANIES: () => string;
    PEOPLE: () => string;
  };
  destroy$: Subject<boolean>;
};

export function WithTaggingFormSection<
  TBase extends Constructor<TaggingFormSectionRequerimentsBase>
>(
  baseClass: TBase
): {
  new (...args: any[]): WithTaggingFormSectionType;
  prototype: WithTaggingFormSectionType;
} & TBase {
  return class WithTaggingFormSectionable extends baseClass {
    constructor(...args: any[]) {
      super(...args);
    }

    _initTagging(
      tags: ITag[],
      topics: ITopic[],
      companies: ICompany[],
      people: IPerson[],
      topicService: TopicService
    ): void {
      if (tags) {
        const tagsControl = this.form.get(this.FORM_PATHS.TAGS());
        tags.forEach((i) => {
          (tagsControl as FormArray).push(new FormControl(i));
        });
      }

      if (topics) {
        const topicControl = this.form.get(this.FORM_PATHS.TOPICS());
        topics.forEach((i) => {
          (topicControl as FormArray).push(new FormControl(i));
        });
      }

      if (companies) {
        const companiesControl = this.form.get(this.FORM_PATHS.COMPANIES());
        companies.forEach((i) => {
          (companiesControl as FormArray).push(new FormControl(i));
        });
      }

      if (people) {
        const peopleControl = this.form.get(this.FORM_PATHS.PEOPLE());
        people.forEach((i) => {
          (peopleControl as FormArray).push(new FormControl(i));
        });
      }

      topicService
        .getFiltered$('')
        .subscribe((topics: ITopicWithTranslations[]) => {
          const detailedTopicsControl = this.form.get(
            this.FORM_PATHS.TOPICS_WITH_TRANSLATIONS()
          );
          topics.forEach((i) => {
            (detailedTopicsControl as FormArray).push(new FormControl(i));
          });
        });
    }

    _initTaggingValueChanges(
      getPreviousTagsValue: () => ITag[] | null,
      getPreviousTopicsValue: () => ITopic[] | null,
      getPreviousCompanyValue: () => ICompany[] | null,
      getPreviousPeopleValue: () => IPerson[] | null
    ): void {
      this.form
        .get(this.FORM_PATHS.TAGS())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue) => {
            if (compareArrays(newValue, getPreviousTagsValue())) {
              this.form.get(this.FORM_PATHS.TAGS()).markAsPristine();
            } else {
              this.form.get(this.FORM_PATHS.TAGS()).markAsDirty();
            }
          },
        });

      this.form
        .get(this.FORM_PATHS.TOPICS())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue) => {
            if (compareArrays(newValue, getPreviousTopicsValue())) {
              this.form.get(this.FORM_PATHS.TOPICS()).markAsPristine();
            } else {
              this.form.get(this.FORM_PATHS.TOPICS()).markAsDirty();
            }
          },
        });

      this.form
        .get(this.FORM_PATHS.COMPANIES())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue) => {
            if (compareArrays(newValue, getPreviousCompanyValue())) {
              this.form.get(this.FORM_PATHS.COMPANIES()).markAsPristine();
            } else {
              this.form.get(this.FORM_PATHS.COMPANIES()).markAsDirty();
            }
          },
        });

      this.form
        .get(this.FORM_PATHS.PEOPLE())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue) => {
            if (compareArrays(newValue, getPreviousPeopleValue())) {
              this.form.get(this.FORM_PATHS.PEOPLE()).markAsPristine();
            } else {
              this.form.get(this.FORM_PATHS.PEOPLE()).markAsDirty();
            }
          },
        });
    }
    _buildTags(): ITag[] {
      return this.form.get(this.FORM_PATHS.TAGS()).value;
    }

    _buildTopics(): ITopic[] {
      return this.form.get(this.FORM_PATHS.TOPICS()).value;
    }

    _buildCompanies(): ICompany[] {
      return this.form.get(this.FORM_PATHS.COMPANIES()).value;
    }

    _buildPeople(): IPerson[] {
      return this.form.get(this.FORM_PATHS.PEOPLE()).value;
    }

    get tags(): ITag[] {
      return this.form.get(this.FORM_PATHS.TAGS()).value;
    }

    get topics(): ITopic[] {
      return this.form.get(this.FORM_PATHS.TOPICS()).value;
    }

    get companies(): ICompany[] {
      return this.form.get(this.FORM_PATHS.COMPANIES()).value;
    }

    get people(): IPerson[] {
      return this.form.get(this.FORM_PATHS.PEOPLE()).value;
    }
  };
}
